<template>
	<div class="container mx-auto relative">
		<PageTitle>Welcome to ProxyHeaven</PageTitle>

		<div class="grid lg:grid-cols-2 xl:grid-cols-3 gap-4 grid-rows-[auto_1fr]" v-if="loaded">
			<div class="bg-gray-800 rounded-lg shadow-sm xl:row-span-2 lg:col-span-2 xl:col-span-1">
				<div class="p-6 lg:p-8 flex items-center">
					<svg class="w-6 h-6 text-yellow-500 mr-4" viewBox="0 0 24 24" fill="currentColor"
						 xmlns="http://www.w3.org/2000/svg">
						<path
							d="M10.486 6.13125L6.98909 0.364219C6.89534 0.138234 6.65159 0 6.38909 0H0.751435C0.144404 0 -0.210909 0.683437 0.137372 1.18031L5.35456 8.62875C6.74534 7.33125 8.51722 6.43594 10.486 6.13125ZM12 7.5C7.44144 7.5 3.75003 11.1914 3.75003 15.75C3.75003 20.3086 7.44378 24 12 24C16.5563 24 20.25 20.3086 20.25 15.75C20.25 11.1914 16.561 7.5 12 7.5ZM16.336 14.8734L14.5603 16.6078L14.9764 19.057C15.0525 19.4906 14.5896 19.8305 14.1968 19.6195L11.9993 18.4655L9.80159 19.6172C9.40901 19.8281 8.94612 19.4883 9.02206 19.0547L9.43808 16.6055L7.66245 14.8711C7.34065 14.5594 7.52347 14.0156 7.96409 13.95L10.4194 13.5926L11.5172 11.3672C11.611 11.168 11.8043 11.0684 11.9977 11.0684C12.191 11.0684 12.3844 11.1703 12.4782 11.3672L13.5797 13.5938L16.035 13.9512C16.4813 14.0156 16.6594 14.5594 16.336 14.8734ZM23.2078 0H17.611C17.3475 0 17.1033 0.138234 16.9678 0.364219L13.5103 6.08766C15.4791 6.39234 17.2547 7.28859 18.6432 8.58984L23.8603 1.14141C24.211 0.683437 23.8547 0 23.2078 0Z"/>
					</svg>
					<div class="font-bold text-xl text-white">{{ dateUtils.getLongMonth(new Date().getMonth()) }}
						Leaderboard
					</div>

					<button class="ml-auto transition-colors hover:text-white" @click="modalOpen = true">
						<InformationCircleIcon class="w-7 h-7"/>
					</button>
				</div>

				<div class="grid pb-4">
					<LeaderboardEntry
						v-for="leader in leaders"
						:leader="leader"
						:mark-as-me="leader.score === ownLeaderboardPlacement?.score && leader.display_name === ownLeaderboardPlacement.display_name"/>
					<div v-if="ownLeaderboardPlacement.position > 5">
						<div class="flex flex-col justify-center align-middle gap-2 mb-3">
							<div class="rounded-full mx-auto bg-gray-600 w-2 h-2" v-for="_ in [0,1,2]"></div>
						</div>
						<LeaderboardEntry :leader="ownLeaderboardPlacement" :mark-as-me="true"/>
					</div>

				</div>
			</div>

			<BlocksCard :blocks="blocks"/>

			<div class="bg-gray-800 rounded-lg shadow-sm flex items-center justify-center py-6">
				<div class="w-20 h-20 rounded-full border-2 border-gray-700 bg-cover bg-center mr-6"
					 :style="`background-image:url(${userStore.avatarURL})`"></div>
				<div>
					<div class="font-bold">Welcome Back</div>
					<div class="font-bold text-xl text-white">{{ userStore.email }}</div>
				</div>
			</div>

			<div class="bg-gray-800 rounded-lg shadow-sm grid grid-rows-[auto_1fr] lg:col-span-2 xl:col-span-2">
				<div
					class="rounded-t-md bg-gradient-to-tr from-green-600 to-lime-400 py-4 px-6 text-white md:flex flex-wrap items-center justify-between">
					<div class="text-xl font-bold mb-3 md:mb-0">Use code "Heaven" for 25% Off</div>
					<a href="https://proxyheaven.io/discount/HEAVEN" target="_blank"
					   class="rounded-md border-2 border-white text-white font-bold py-3 px-6">
						View Promotion
					</a>
				</div>

				<div class="grid lg:grid-cols-2 2xl:grid-cols-3">
					<div class="grid border-b border-gray-700 lg:border-b-0 lg:border-r">
						<DatePicker :date-action="pickDate" :dates="drops.dates" @loadMonth="loadMonth"/>
					</div>

					<div class="2xl:col-span-2 p-6">
						<div v-if="date && selectedDrops.length > 0">
							<h3 class="text-center font-bold text-white text-xl mb-6">Drops for {{ date }}</h3>

							<div>
								<Drop v-for="drop in selectedDrops" :drop="drop"/>
							</div>
						</div>
						<div v-else class="text-center p-6">
							<CloudIcon class="w-12 h-12 text-gray-600 mx-auto mb-4"/>
							<p class="font-bold">{{ date }}</p>
							<p>No drops to display</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<Teleport to="body">
		<Modal :open="modalOpen" :closeFunc="closeModal" title="How Do Leaderboards Work?">
			<p>The Leaderboard is a monthly friendly competition between ProxyHeaven customers.</p>
			<p>You need to
				<RouterLink to="/user/settings" class="text-white hover:underline">connect your Discord</RouterLink>
				to your dashboard account & then can start sharing success in our
				success channel. Depending on the success, every post gets points awarded (Tweets earn double points).
			</p>
			<p>The top 5 can win great prizes, which change every month.</p>
			<br>
			<h2 class="text-xl text-white font-semibold mb-2">Prizes this month:</h2>
			<div class="border-2 border-green-700 rounded-md py-3 flex items-center mb-3">
				<div class="w-24 font-bold text-green-500 text-center max-w-24 flex-none">#1</div>
				<div class="text-left flex-auto pr-3 flex-1">
					<h5 class="font-bold text-white">First Place</h5>
					<p class="text-sm">{{ prices.find(v => v.position === 'first')?.price }}</p>
				</div>
			</div>

			<div class="border-2 border-gray-700 rounded-md py-3 flex items-center mb-3">
				<div class="w-24 font-bold text-gray-500 text-center max-w-24 flex-none">#2</div>
				<div class="text-left flex-auto pr-3 flex-1">
					<h5 class="font-bold text-white">Second Place</h5>
					<p class="text-sm">{{ prices.find(v => v.position === 'second')?.price }}</p>
				</div>
			</div>

			<div class="border-2 border-gray-700 rounded-md py-3 flex items-center mb-3">
				<div class="w-24 font-bold text-gray-500 text-center max-w-24 flex-none">#3</div>
				<div class="text-left flex-auto pr-3 flex-1">
					<h5 class="font-bold text-white">Third Place</h5>
					<p class="text-sm">{{ prices.find(v => v.position === 'third')?.price }}</p>
				</div>
			</div>

			<div class="border-2 border-gray-700 rounded-md py-3 flex items-center mb-3">
				<div class="w-24 font-bold text-gray-500 text-center max-w-24 flex-none">#4</div>
				<div class="text-left flex-auto pr-3 flex-1">
					<h5 class="font-bold text-white">Fourth Place</h5>
					<p class="text-sm">{{ prices.find(v => v.position === 'top_five')?.price }}</p>
				</div>
			</div>

			<div class="border-2 border-gray-700 rounded-md py-3 flex items-center mb-3">
				<div class="w-24 font-bold text-gray-500 text-center max-w-24 flex-none">#5</div>
				<div class="text-left flex-auto pr-3 flex-1">
					<h5 class="font-bold text-white">Fifth Place</h5>
					<p class="text-sm">{{ prices.find(v => v.position === 'top_five')?.price }}</p>
				</div>
			</div>

		</Modal>
		<Modal v-if="popup" :title="popup!.title" :close-func="markPopupAsRead" :open="isPopupAvailable">
			<template #default>
				<div class="rendered-markdown prose-invert lg:prose-xl" v-html="renderedPopupHtml"/>
			</template>
		</Modal>
	</Teleport>
</template>
<script lang="ts" setup>

import {onMounted, reactive, ref} from 'vue'
import {
	Drop as DropEntity,
	getDrops,
	getHomepage,
	IspBlock,
	LeaderboardPrice2,
	LeaderboardUser2,
	Popup,
	markPopupAsRead as apiMarkPopupAsRead, getLatestPopup
} from '../api'
import {usePageStore, useUserStore} from '../stores'
import {CloudIcon} from '@heroicons/vue/24/solid'
import {InformationCircleIcon} from '@heroicons/vue/24/outline'
import LeaderboardEntry from '../components/LeaderboardEntry.vue'
import dateUtils from '../util/dateUtils'
import DatePicker from '../components/DatePicker.vue'
import Modal from '../components/Modal.vue'
import Drop from '../components/Drop.vue'
import BlocksCard from '../components/BlocksCard.vue'
import PageTitle from '../components/layout/PageTitle.vue'
import {useToast} from "vue-toastification";
import MarkdownIt from "markdown-it";
import '../../../shared/markdown.css'


const toast = useToast()
const pageStore = usePageStore()
const userStore = useUserStore()
const modalOpen = ref(false)
const closeModal = () => {
	modalOpen.value = false
}

const date = ref()
const monthYear = ref<string>('')
const loaded = ref(false)

const pickDate = (day: number, month: number, year: number) => {
	date.value = `${dateUtils.getDateOrdinal(day)} of ${dateUtils.getLongMonth(month)}, ${year}`
	selectedDrops.value = drops.dates[`${year}-${month}-${day}`] || []
}

const selectedDrops = ref<DropEntity[]>([])

const drops = reactive<{
	dates: {
		[date: string]: DropEntity[]
	},
	cachedMonths: {
		[date: string]: boolean
	}
}>({
	dates: {},
	cachedMonths: {}
})
const blocks = ref<IspBlock[]>([])
const prices = ref<LeaderboardPrice2[]>([])
const leaders = ref<LeaderboardUser2[]>([])
const ownLeaderboardPlacement = ref<LeaderboardUser2>({
	display_name: "", position: 0, rank: "", score: 0
})

async function homepageLoad() {
	const today = new Date()
	drops.cachedMonths[`${today.getFullYear()}-${today.getMonth() + 1}`] = true
	const homepage = await getHomepage()
	for (let i = 0; i < (homepage.data.drops?.length || 0); i++) {
		let drop = homepage.data.drops![i]
		let date = new Date(drop.release_date)
		const year = date.getFullYear()
		const month = date.getMonth()
		const day = date.getDate()
		drops.dates[`${year}-${month}-${day}`] = drops.dates[`${year}-${month}-${day}`] || []
		drops.dates[`${year}-${month}-${day}`].push(drop)
	}
	leaders.value = homepage.data.leaderboard?.leaderboard
	ownLeaderboardPlacement.value = homepage.data.leaderboard?.own_placement
	blocks.value = homepage.data.blocks || []
	prices.value = homepage.data.leaderboard_prices || []
	loaded.value = true
	pageStore.setLoadingState(false)
	const now = new Date()
	const year = now.getFullYear()
	const month = now.getMonth()
	const day = now.getDate()
	monthYear.value = `${month + 1}-${year}`
	pickDate(day, month, year)
}

const isPopupAvailable = ref(false)
const popup = ref<Popup>()
const popupLoadedTimestamp = ref(0)
const renderedPopupHtml = ref('')

async function popupLoad() {
	const p = await getLatestPopup()
	if (p.status === 200) {
		popupLoadedTimestamp.value = Date.now()
		popup.value = p.data
		isPopupAvailable.value = true
		renderedPopupHtml.value = new MarkdownIt({
			html: true,
		}).render(popup.value?.content)
	}
}

function markPopupAsRead() {
	isPopupAvailable.value = false
	if (!popup.value) return
	const tenSecondsAgo = Date.now() - 1000 * 5
	if (popupLoadedTimestamp.value > tenSecondsAgo) {
		// User has not read the popup for 5 seconds, so it is not marked as read
		return
	}
	apiMarkPopupAsRead().then(res => {
		if (res.status !== 200) {
			console.error(res.data.message)
		}
	}).catch(err => {
		console.error(err)
	})
}

onMounted(() => {
	homepageLoad()
	popupLoad()
})

async function loadMonth(year: number, month: number) {
	if (`${month}-${year}` === monthYear.value) return
	monthYear.value = `${month}-${year}`
	if (drops.cachedMonths[`${year}-${month}`]) {
		pickDate(1, month - 1, year)
		return
	}
	await getDrops(year, month).then(res => {
		if (res.status === 200) {
			drops.cachedMonths[`${year}-${month}`] = true
			for (const drop of res.data) {
				let date = new Date(drop.release_date)
				const year = date.getFullYear()
				const month = date.getMonth()
				const day = date.getDate()
				drops.dates[`${year}-${month}-${day}`] = drops.dates[`${year}-${month}-${day}`] || []
				drops.dates[`${year}-${month}-${day}`].push(drop)
			}
			pickDate(1, month - 1, year)
		} else {
			toast.error(res.data.message)
		}
	}).catch(err => {
		console.error(err)
		toast.error("An error occurred while loading drops")
	})
}

</script>
